import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getLimits } from '@/api/tutor'
import { getStudentLimits } from '@/api/student'

/**
 * Subscription limits for the signed-in user.
 *
 * This previously kept only minSessionCharge / minSessionDurationMinutes and discarded every
 * usage count the endpoint returns. The counts are kept now so the UI can show usage, disable
 * actions at the cap, and explain the over-limit case.
 *
 * Each entry: { used, max, canAdd, isOverLimit }
 *   max: null      -> unlimited
 *   canAdd: false  -> block the "add" action
 *   isOverLimit    -> already ABOVE a reduced cap. Existing items keep working (enforcement is forward-only), so this needs a reassuring message, not an error.
 */
export const usePlatformLimitsStore = defineStore('platformLimits', () => {
  const minSessionCharge = ref(30)
  const minSessionDurationMinutes = ref(60)

  const limits = ref({})
  const features = ref({})
  const packageName = ref('')
  const loaded = ref(false)

  function applyPayload(data) {
    const payloadLimits = data?.limits || {}
    limits.value = payloadLimits
    features.value = data?.features || {}
    packageName.value = data?.packageName || ''
    minSessionCharge.value = payloadLimits.minSessionCharge ?? 30
    minSessionDurationMinutes.value = payloadLimits.minSessionDurationMinutes ?? 60
  }

  // role: 'tutor' | 'student' — hits the matching limits endpoint
  async function fetchIfNeeded(role = 'tutor') {
    if (loaded.value) return
    try {
      const res = role === 'student' ? await getStudentLimits() : await getLimits()
      applyPayload(res?.data?.data)
      loaded.value = true
    } catch {
      // keep defaults — these are advisory in the UI; the server still enforces them
    }
  }

  // Call after anything that changes usage (new note, new booking) or after a plan change
  async function refresh(role = 'tutor') {
    loaded.value = false
    await fetchIfNeeded(role)
  }

  const entry = (key) => limits.value?.[key] || { used: 0, max: null, canAdd: true, isOverLimit: false }
  const canAdd = (key) => entry(key).canAdd !== false
  const isOverLimit = (key) => entry(key).isOverLimit === true

  return {
    minSessionCharge,
    minSessionDurationMinutes,
    limits,
    features,
    packageName,
    loaded,
    fetchIfNeeded,
    refresh,
    entry,
    canAdd,
    isOverLimit,
  }
})
